import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, CardNumberElement, CardExpiryElement, CardCvcElement,
  useStripe, useElements,
} from '@stripe/react-stripe-js';

const api = axios.create({ withCredentials: true });

const STRIPE_FIELD = {
  style: { base: { fontSize: '15px', color: '#333', '::placeholder': { color: '#9aa0ad' } } },
};

/* Icona carta generica (nessun asset esterno) */
const CardIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="4" fill="#F1F1F1" />
    <rect x="8" y="10" width="16" height="11" rx="1.4" stroke="#333" strokeWidth="1" />
    <rect x="8" y="13" width="16" height="2" fill="#333" />
  </svg>
);

/* Icona PayPal generica */
const PayPalIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="4" fill="#F1F1F1" />
    <path d="M13.7 9h4.1c2 0 3.4 1 3.1 3.1-.3 2.4-1.9 3.4-4.1 3.4h-1.1c-.3 0-.5.2-.55.5l-.5 3.2c-.03.2-.2.3-.4.3h-1.9c-.25 0-.4-.2-.36-.44L13.35 9.3A.4.4 0 0 1 13.7 9Z" fill="#27346A" />
    <path d="M15.4 11.6h3.7c1.7 0 2.9.9 2.6 2.9-.25 2-1.7 3-3.7 3h-1c-.28 0-.5.2-.54.48l-.55 3.5c-.03.2-.2.34-.4.34h-1.7c-.24 0-.4-.2-.36-.43l1.5-9.4a.4.4 0 0 1 .4-.34Z" fill="#2790C3" />
  </svg>
);

/* Loghi circuiti (generici) accanto al numero carta */
const SchemeLogos = () => (
  <span className="nfx-pm-schemes" aria-hidden="true">
    <svg viewBox="0 0 26 16" width="26" height="16"><rect x="0.25" y="0.25" width="25.5" height="15.5" rx="1.75" fill="#fff" stroke="#e0e0e0" strokeWidth="0.5" /><path d="M11 6h1.4l-.9 5h-1.4l.9-5Zm5.8 0h-1.2c-.27 0-.5.16-.6.4L13 11h1.4l.28-.8h1.7l.16.8H18l-1.2-5Zm-1.5 2.9.7-1.95.35 1.95h-1.05ZM9.9 6l-1.35 3.4-.15-.75c-.3-.9-1-1.6-1.9-1.95L7.7 11h1.45L11.3 6H9.9Zm-4.2 0H3.5v.13c1 .22 1.85.75 2.35 1.6L5.7 6Z" fill="#224dba" /></svg>
    <svg viewBox="0 0 26 16" width="26" height="16"><rect width="26" height="16" rx="2" fill="#353a48" /><circle cx="10.4" cy="8" r="4.2" fill="#e82c30" /><circle cx="15.6" cy="8" r="4.2" fill="#eca21d" /><path d="M13 4.9a4.2 4.2 0 0 0 0 6.2 4.2 4.2 0 0 0 0-6.2Z" fill="#f36d1e" /></svg>
  </span>
);

function CardForm({ clientSecret, onClose, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [holder, setHolder] = useState('');
  const [numOk, setNumOk] = useState(false);
  const [expOk, setExpOk] = useState(false);
  const [cvcOk, setCvcOk] = useState(false);
  const [predefinito, setPredefinito] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSave = !!stripe && numOk && expOk && cvcOk && holder.trim().length > 1 && !busy;

  const save = async () => {
    if (!stripe || !elements) return;
    setBusy(true); setError(null);
    try {
      const card = elements.getElement(CardNumberElement);
      const res = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { name: holder } },
      });
      if (res.error) { setError(res.error.message || 'Carta non verificata.'); setBusy(false); return; }
      if (res.setupIntent?.status !== 'succeeded') { setError('Verifica della carta non completata.'); setBusy(false); return; }
      const pmId = res.setupIntent.payment_method;
      if (predefinito && pmId) {
        try { await api.put(`/api/metodi-pagamento/${pmId}/predefinito`); } catch (_) { /* non bloccante */ }
      }
      onDone();
    } catch (e) {
      setError('Errore durante il salvataggio della carta. Riprova.');
      setBusy(false);
    }
  };

  return (
    <>
      <div className="nfx-modal__body">
        <h3 className="nfx-pm-subtitle">Seleziona il metodo di pagamento da aggiungere:</h3>

        <div className="nfx-pm-method is-selected">
          <div className="nfx-pm-method__head">
            <span className="nfx-pm-radio is-on" aria-hidden="true" />
            <span className="nfx-pm-icon"><CardIcon /></span>
            <span className="nfx-pm-name">Carta bancaria</span>
            <span className="nfx-pm-pill">Consigliato</span>
          </div>

          <div className="nfx-pm-body">
            <label className="nfx-cardfld">
              <span className="nfx-cardfld__lbl">Nome del titolare</span>
              <input className="nfx-cardfld__in" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Nome Cognome" />
            </label>

            <label className="nfx-cardfld">
              <span className="nfx-cardfld__lbl">Numero di carta</span>
              <div className="nfx-cardbox nfx-cardbox--num">
                <CardNumberElement options={{ ...STRIPE_FIELD, showIcon: false }} onChange={(e) => setNumOk(e.complete)} />
                <SchemeLogos />
              </div>
            </label>

            <div className="nfx-pm-row">
              <label className="nfx-cardfld">
                <span className="nfx-cardfld__lbl">Data di scadenza</span>
                <div className="nfx-cardbox"><CardExpiryElement options={STRIPE_FIELD} onChange={(e) => setExpOk(e.complete)} /></div>
              </label>
              <label className="nfx-cardfld">
                <span className="nfx-cardfld__lbl">Codice di sicurezza</span>
                <div className="nfx-cardbox"><CardCvcElement options={STRIPE_FIELD} onChange={(e) => setCvcOk(e.complete)} /></div>
              </label>
            </div>

            <label className="nfx-pm-check">
              <input type="checkbox" checked={predefinito} onChange={(e) => setPredefinito(e.target.checked)} />
              <span>Definire questo metodo di pagamento come predefinito</span>
            </label>

            <p className="nfx-cardnote">La carta viene verificata subito, <b>senza alcun addebito</b>. Verrà usata per il rinnovo dei tuoi prodotti e il pagamento delle fatture.</p>
            {error && <div className="nfx-carderr">{error}</div>}
          </div>
        </div>

        <div className="nfx-pm-method is-disabled" aria-disabled="true">
          <div className="nfx-pm-method__head">
            <span className="nfx-pm-radio" aria-hidden="true" />
            <span className="nfx-pm-icon"><PayPalIcon /></span>
            <span className="nfx-pm-name">PayPal</span>
            <span className="nfx-pm-pill nfx-pm-pill--muted">Non ancora disponibile</span>
          </div>
        </div>
      </div>

      <div className="nfx-modal__foot">
        <button className="nfx-btn-sec" onClick={onClose}>Annullare</button>
        <button className={`nfx-btn-pri${canSave ? '' : ' is-disabled'}`} disabled={!canSave} onClick={save}>
          {busy ? 'Attendere…' : 'Aggiungi'}
        </button>
      </div>
    </>
  );
}

export default function AddCardModal({ onClose, onSaved }) {
  const [state, setState] = useState({ loading: true, error: null, clientSecret: null, stripePromise: null });

  useEffect(() => {
    let alive = true;
    api.post('/api/metodi-pagamento/setup-intent')
      .then((r) => {
        if (!alive) return;
        const { clientSecret, publishableKey } = r.data || {};
        if (!clientSecret || !publishableKey) {
          setState({ loading: false, error: 'Configurazione dei pagamenti non disponibile.', clientSecret: null, stripePromise: null });
          return;
        }
        setState({ loading: false, error: null, clientSecret, stripePromise: loadStripe(publishableKey) });
      })
      .catch(() => {
        if (alive) setState({ loading: false, error: 'Impossibile inizializzare l\'aggiunta della carta. Riprova.', clientSecret: null, stripePromise: null });
      });
    return () => { alive = false; };
  }, []);

  return (
    <div className="nfx-modal-scrim" onClick={onClose}>
      <div className="nfx-modal nfx-modal--pm" onClick={(e) => e.stopPropagation()}>
        <div className="nfx-modal__head">
          <span className="nfx-modal__title">Aggiungi una modalità di pagamento</span>
          <button className="nfx-modal__close" onClick={onClose} aria-label="Chiudi"><X size={20} /></button>
        </div>
        {state.loading ? (
          <div className="nfx-modal__body"><div className="nfx-loading">Caricamento…</div></div>
        ) : state.error ? (
          <>
            <div className="nfx-modal__body"><div className="nfx-carderr">{state.error}</div></div>
            <div className="nfx-modal__foot"><button className="nfx-btn-sec" onClick={onClose}>Chiudi</button></div>
          </>
        ) : (
          <Elements stripe={state.stripePromise} options={{ locale: 'it' }}>
            <CardForm clientSecret={state.clientSecret} onClose={onClose} onDone={() => onSaved?.()} />
          </Elements>
        )}
      </div>
    </div>
  );
}
